import type {
  ChatService,
  ChatRequest,
  TitleGenerationRequest,
  TitleGenerationResponse,
  RealChatServiceConfig,
} from '../types';

/**
 * Default configuration for RealChatService
 */
const DEFAULT_CONFIG: Required<RealChatServiceConfig> = {
  apiBaseUrl: 'http://localhost:8000',
  timeout: 30000,
  debug: false,
};

/**
 * Custom error class for chat service errors with additional context
 */
export class ChatServiceError extends Error {
  public readonly statusCode?: number;
  public readonly isTimeout: boolean;
  public readonly isNetworkError: boolean;
  public readonly cause?: Error;

  constructor(
    message: string,
    options?: {
      statusCode?: number;
      isTimeout?: boolean;
      isNetworkError?: boolean;
      cause?: Error;
    }
  ) {
    super(message);
    this.name = 'ChatServiceError';
    this.statusCode = options?.statusCode;
    this.isTimeout = options?.isTimeout ?? false;
    this.isNetworkError = options?.isNetworkError ?? false;
    this.cause = options?.cause;
  }
}

/**
 * Real implementation of the ChatService interface.
 * Connects to mamba-server backend for AI chat completions.
 */
export class RealChatService implements ChatService {
  private config: Required<RealChatServiceConfig>;

  constructor(config: RealChatServiceConfig) {
    this.config = { ...DEFAULT_CONFIG, ...config };

    if (!this.config.apiBaseUrl) {
      throw new Error('RealChatService: apiBaseUrl is required');
    }
  }

  /**
   * Stream a chat response from the backend
   * @param request - Chat request with messages, model, and optional abort signal
   * @returns ReadableStream of SSE data
   */
  async streamChat(request: ChatRequest): Promise<ReadableStream<Uint8Array>> {
    if (this.config.debug) {
      console.log('[RealChatService] streamChat called:', {
        messageCount: request.messages.length,
        model: request.model,
        tools: request.tools,
      });
    }

    // Handle empty messages array
    if (!request.messages.length) {
      return this.createEmptyStream();
    }

    const endpoint = `${this.config.apiBaseUrl}/chat/completions`;

    // Build request body matching ChatCompletionRequest schema
    const body: Record<string, unknown> = {
      messages: request.messages,
      model: request.model || 'openai/gpt-4o',
      stream: true,
    };

    // Include tools array if provided
    if (request.tools && request.tools.length > 0) {
      body.tools = request.tools;
    }

    // Create timeout controller
    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => {
      timeoutController.abort();
    }, this.config.timeout);

    // Combine user abort signal with timeout
    const combinedSignal = request.abortSignal
      ? this.combineAbortSignals(request.abortSignal, timeoutController.signal)
      : timeoutController.signal;

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
        },
        body: JSON.stringify(body),
        signal: combinedSignal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw this.createHttpError(response.status, response.statusText, errorText);
      }

      if (!response.body) {
        throw new ChatServiceError('Server returned empty response');
      }

      if (this.config.debug) {
        console.log('[RealChatService] Stream started');
      }

      return response.body;
    } catch (error) {
      clearTimeout(timeoutId);

      // Handle user abort
      if (request.abortSignal?.aborted) {
        if (this.config.debug) {
          console.log('[RealChatService] Request aborted by user');
        }
        throw error;
      }

      // Handle timeout
      if (timeoutController.signal.aborted) {
        throw new ChatServiceError('Request timed out. Please try again.', {
          isTimeout: true,
          cause: error instanceof Error ? error : undefined,
        });
      }

      // Handle network errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new ChatServiceError('Unable to connect to server. Please check your connection.', {
          isNetworkError: true,
          cause: error,
        });
      }

      // Re-throw ChatServiceError as-is
      if (error instanceof ChatServiceError) {
        throw error;
      }

      // Wrap unknown errors
      const message = error instanceof Error ? error.message : 'Unknown error';
      throw new ChatServiceError(`Connection error: ${message}`, {
        isNetworkError: true,
        cause: error instanceof Error ? error : undefined,
      });
    }
  }

  /**
   * Generate a title for a conversation.
   *
   * NOTE: Title generation via backend is deferred to a future phase.
   * See PRD Section 11 (Future Enhancements) for planned implementation.
   * Currently returns a fallback response that triggers client-side title generation.
   */
  async generateTitle(
    request: TitleGenerationRequest
  ): Promise<TitleGenerationResponse> {
    if (this.config.debug) {
      console.log('[RealChatService] generateTitle called (stub):', request);
    }

    // TODO: Implement real title generation endpoint when available (PRD Section 11)
    return {
      title: 'New Conversation',
      useFallback: true,
    };
  }

  /**
   * Create an HTTP error with user-friendly message
   */
  private createHttpError(status: number, statusText: string, body: string): ChatServiceError {
    if (status >= 500) {
      return new ChatServiceError(
        `Server error (${status}). Please try again later.`,
        { statusCode: status }
      );
    }

    if (status === 429) {
      return new ChatServiceError(
        'Too many requests. Please wait a moment and try again.',
        { statusCode: status }
      );
    }

    if (status === 401 || status === 403) {
      return new ChatServiceError(
        'Authentication error. Please check your configuration.',
        { statusCode: status }
      );
    }

    if (status >= 400) {
      // Include body content for 4xx errors as it may contain useful info
      const detail = body ? `: ${body.slice(0, 100)}` : '';
      return new ChatServiceError(
        `Request error (${status})${detail}`,
        { statusCode: status }
      );
    }

    return new ChatServiceError(
      `Unexpected response (${status} ${statusText})`,
      { statusCode: status }
    );
  }

  /**
   * Combine multiple abort signals into one
   */
  private combineAbortSignals(...signals: AbortSignal[]): AbortSignal {
    const controller = new AbortController();

    for (const signal of signals) {
      if (signal.aborted) {
        controller.abort(signal.reason);
        return controller.signal;
      }

      signal.addEventListener('abort', () => {
        controller.abort(signal.reason);
      }, { once: true });
    }

    return controller.signal;
  }

  /**
   * Create an empty stream for edge cases
   */
  private createEmptyStream(): ReadableStream<Uint8Array> {
    return new ReadableStream({
      start(controller) {
        controller.enqueue(
          new TextEncoder().encode('data: {"type":"finish"}\n\n')
        );
        controller.close();
      },
    });
  }
}

/**
 * Create a RealChatService instance
 */
export function createRealChatService(
  config: RealChatServiceConfig
): ChatService {
  return new RealChatService(config);
}
